import { Inject, Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { getFileNameExtension, isNullOrUndefined, isValidFunction, isValidString } from '@pineapplelab/util';
import { Project } from '../../models/project';
import { IUploadTrackerConfig } from '../../models/util/uploadFileTrackerConfig';
import { IVideoVendorService } from '../../models/util/videoVendor';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { environment } from '../../../environments/environment';
import { BucketVendors } from '../../enums/bucketVendors.enum';
import { FirebaseService } from './vendors/firebase.service';
import { AwsService } from './vendors/aws.service';
import { base64toBlob, blobToFile } from '../../utils/utils';

const ATTESTATION_SIGNATURES_PATH = 'attestation-signatures';

@Injectable({
	providedIn: 'root'
})
export class StorageService {

	private videoUploaderService: IVideoVendorService;

	constructor(
		private fireStorage: AngularFireStorage,
		private firebaseService: FirebaseService,
		private awsService: AwsService
	) {
		this.setVideoUploaderService(environment.bucket.vendor);
	}

	setVideoUploaderService(vendor: BucketVendors): void {
		if (!isNullOrUndefined(vendor)) {
			this.videoUploaderService = vendor === BucketVendors.AWS ? this.awsService : this.firebaseService;
		}
	}

	async uploadFile(path: string, file: File, metadata?: firebase.default.storage.UploadMetadata) {
		if (isNullOrUndefined(metadata)) {
			metadata = { contentType: file.type };
		}
		const r = await this.fireStorage.upload(path, file, metadata)
		return await r.ref.getDownloadURL();
	}

	uploadFileWithListener(config: IUploadTrackerConfig): Subscription {
		if (!this.isUploadWithTrackerValid(config)) {
			throw new Error('provided config for video upload with tracker is invalid');
		}
		return this.videoUploaderService.uploadFileWithListener(config);
	}

	async saveOrRemoveImagesIfNecessary(file: File, commonPath: string) {
		let fileUrl: string;
		if (!isNullOrUndefined(file)) {
			const extension = getFileNameExtension(file.name);
			const path = `${commonPath}/image.${extension}`;
			const contentType = file.type || `image/${extension}`;
			fileUrl = await this.uploadFile(path, file, { contentType, cacheControl: 'max-age=3600' });
		} else if (file === null) {
			fileUrl = null;
		}
		return fileUrl;
	}

	async saveOrRemoveVideosIfNecessary(id: string, file: File, modelPath: string, projectId: string) {
		let finalPath = null;
		const commonPath = `${Project.PATH}/${projectId}/${modelPath}/${id}`;
		if (!isNullOrUndefined(file)) {
			const path = `${commonPath}/video.${getFileNameExtension(file.name)}`;
			finalPath = await this.uploadFile(path, file, { contentType: 'video/mp4' });
		}
		return finalPath;
	}

	getVideoExtension(path: string) {
		let delimiter = "/video.";

		let index = path.indexOf(delimiter);
		return path.substring(index + delimiter.length);
	}

	getImageExtension(path: string) {
		let delimiter = "/image.";

		let index = path.indexOf(delimiter);
		return path.substring(index + delimiter.length);
	}

	cancelUpload(): void {
		this.videoUploaderService._cancelUpload$.next();
	}

	async saveAttestationSignature(dataUrl: string, userId: string, categoryEnrollmentId: string, projectId: string): Promise<string> {
		const contentType = 'image/png';
		const file = blobToFile(base64toBlob(dataUrl, contentType), 'signature.png');
		const path = `${Project.PATH}/${projectId}/${ATTESTATION_SIGNATURES_PATH}/${userId}/${categoryEnrollmentId}/signature.png`;
		return this.uploadFile(path, file, { contentType });
	}

	async saveOrRemoveMediaIfNecessary(id: string, file: File, modelPath: string, projectId: string) {
		let finalPath = null;
		const commonPath = `${Project.PATH}/${projectId}/${modelPath}/${id}`;
		if (!isNullOrUndefined(file)) {
			const path = `${commonPath}/media.${getFileNameExtension(file.name)}`;
			finalPath = await this.uploadFile(path, file, { contentType: file?.type });
		}
		return finalPath;
	}

	private isUploadWithTrackerValid(config: IUploadTrackerConfig): boolean {
		return config?.file && (config.file instanceof File) && isValidString(config?.path) && isValidFunction(config?.onProgress) && isValidFunction(config?.onSuccess);
	}

	async transcodeVideo(videoId: string): Promise<void> {
		return this.videoUploaderService.transcode(videoId);
	}

}
